import type { Theme } from './types';

export const themeTokenNames = {
  colors: {
    primary: '--ui-color-primary',
    primaryHover: '--ui-color-primary-hover',
    primaryActive: '--ui-color-primary-active',
    secondary: '--ui-color-secondary',
    secondaryHover: '--ui-color-secondary-hover',
    secondaryActive: '--ui-color-secondary-active',
    background: '--ui-color-background',
    surface: '--ui-color-surface',
    surfaceHover: '--ui-color-surface-hover',
    text: '--ui-color-text',
    textMuted: '--ui-color-text-muted',
    textInverse: '--ui-color-text-inverse',
    border: '--ui-color-border',
    borderLight: '--ui-color-border-light',
    success: '--ui-color-success',
    warning: '--ui-color-warning',
    error: '--ui-color-error',
    info: '--ui-color-info',
  },
  effects: {
    borderWidth: '--ui-border-width',
    borderRadius: '--ui-border-radius',
    borderRadiusSmall: '--ui-border-radius-sm',
    borderRadiusLarge: '--ui-border-radius-lg',
    shadow: '--ui-shadow',
    shadowHover: '--ui-shadow-hover',
    shadowActive: '--ui-shadow-active',
    blur: '--ui-blur',
    transition: '--ui-transition',
    transitionFast: '--ui-transition-fast',
    hoverTranslateX: '--ui-hover-translate-x',
    hoverTranslateY: '--ui-hover-translate-y',
  },
} as const;

export type ThemeCssVarMap = Record<string, string>;

export function buildThemeCssVarMap(theme: Theme): ThemeCssVarMap {
  const vars: ThemeCssVarMap = {};
  const { colors, effects } = theme;
  const names = themeTokenNames;

  vars[names.colors.primary] = colors.primary;
  vars[names.colors.primaryHover] = colors.primaryHover;
  vars[names.colors.primaryActive] = colors.primaryActive;
  vars[names.colors.secondary] = colors.secondary;
  vars[names.colors.secondaryHover] = colors.secondaryHover;
  vars[names.colors.secondaryActive] = colors.secondaryActive;
  vars[names.colors.background] = colors.background;
  vars[names.colors.surface] = colors.surface;
  vars[names.colors.surfaceHover] = colors.surfaceHover;
  vars[names.colors.text] = colors.text;
  vars[names.colors.textMuted] = colors.textMuted;
  vars[names.colors.textInverse] = colors.textInverse;
  vars[names.colors.border] = colors.border;
  vars[names.colors.borderLight] = colors.borderLight;
  vars[names.colors.success] = colors.success;
  vars[names.colors.warning] = colors.warning;
  vars[names.colors.error] = colors.error;
  vars[names.colors.info] = colors.info;

  vars[names.effects.borderWidth] = effects.borderWidth;
  vars[names.effects.borderRadius] = effects.borderRadius;
  vars[names.effects.borderRadiusSmall] = effects.borderRadiusSmall;
  vars[names.effects.borderRadiusLarge] = effects.borderRadiusLarge;
  vars[names.effects.shadow] = effects.shadow;
  vars[names.effects.shadowHover] = effects.shadowHover;
  vars[names.effects.shadowActive] = effects.shadowActive;
  vars[names.effects.blur] = effects.blur;
  vars[names.effects.transition] = effects.transition;
  vars[names.effects.transitionFast] = effects.transitionFast;
  vars[names.effects.hoverTranslateX] = effects.hoverTranslateX;
  vars[names.effects.hoverTranslateY] = effects.hoverTranslateY;

  return vars;
}

export function themeVarMapToCss(vars: ThemeCssVarMap): string {
  const lines = Object.entries(vars).map(([key, value]) => `  ${key}: ${value};`);
  return `\n${lines.join('\n')}\n`;
}

export const themeTokenVars = {
  colors: Object.fromEntries(
    Object.entries(themeTokenNames.colors).map(([key, value]) => [key, `var(${value})`])
  ) as Record<keyof typeof themeTokenNames.colors, string>,
  effects: Object.fromEntries(
    Object.entries(themeTokenNames.effects).map(([key, value]) => [key, `var(${value})`])
  ) as Record<keyof typeof themeTokenNames.effects, string>,
};
